import { useGet } from "@/lib/api";
import { History } from "lucide-react";

const H = [["1w", "1 week"], ["4w", "4 weeks"], ["12w", "12 weeks"]];
const DIR = { gold_above: ["Gold crossed ABOVE dollar → BUY bias", "#10B981"], gold_below: ["Gold crossed BELOW dollar → SELL bias", "#EF4444"] };
const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(2)}%`);
const col = (v) => (v == null ? "#6B7280" : v > 0 ? "#10B981" : v < 0 ? "#EF4444" : "#9CA3AF");
const day = (iso) => new Date(iso.length === 16 ? iso + ":00Z" : iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export function CrossingBacktest({ refreshKey }) {
  const { data: d, loading, error } = useGet("/market-drivers/dxy-gold/crossings-backtest", refreshKey);
  return (
    <div data-testid="crossing-backtest-card" className="rounded-2xl border border-white/10 bg-[#0B0F17]/80 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-1">
        <History size={16} className="text-[#FB923C]" />
        <h3 className="text-base md:text-lg font-semibold text-white">Crossing backtest · what gold did next</h3>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-3">
        {d ? `${d.crossings} crossings on 4H · ${day(d.span.from)} → ${day(d.span.to)} · rolling 60-day rebase` : "Gold's return 1 / 4 / 12 weeks after each 4H Gold-vs-DXY crossing"}
      </p>
      {loading && !d && <div className="text-xs text-[#9CA3AF]">Loading 2 years of 4H bars…</div>}
      {error && <div className="text-xs text-[#EF4444]">Backtest unavailable right now.</div>}
      {d?.warming_up && <div data-testid="crossing-warming" className="text-xs text-[#F59E0B]">Warming up — the 2-year backtest is being computed in the background.</div>}
      {d && !d.warming_up && (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(d.summary).map(([dir, row]) => (
              <div key={dir} data-testid={`crossing-summary-${dir}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <div className="text-xs font-semibold mb-2" style={{ color: DIR[dir][1] }}>{DIR[dir][0]}</div>
                <table className="w-full text-[11px] font-mono-num">
                  <thead><tr className="text-[#6B7280]"><th className="text-left font-normal">After</th><th className="font-normal">n</th><th className="font-normal">Avg</th><th className="font-normal">Median</th><th className="font-normal">Favourable</th></tr></thead>
                  <tbody>
                    {H.map(([k, label]) => (
                      <tr key={k} className="border-t border-white/5">
                        <td className="py-1 text-[#D1D5DB]">{label}</td>
                        <td className="text-center text-[#9CA3AF]">{row[k].n}</td>
                        <td className="text-center" style={{ color: col(row[k].avg) }}>{pct(row[k].avg)}</td>
                        <td className="text-center" style={{ color: col(row[k].median) }}>{pct(row[k].median)}</td>
                        <td className="text-center" style={{ color: row[k].favourable_pct == null ? "#6B7280" : row[k].favourable_pct >= 55 ? "#10B981" : row[k].favourable_pct <= 45 ? "#EF4444" : "#F59E0B" }}>
                          {row[k].favourable_pct == null ? "—" : `${row[k].favourable_pct}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <div className="mt-3 overflow-x-auto">
            <table data-testid="crossing-events-table" className="w-full text-[11px] font-mono-num whitespace-nowrap">
              <thead><tr className="text-[#6B7280]"><th className="text-left font-normal py-1 pr-3">Date</th><th className="text-left font-normal pr-3">Crossing</th><th className="font-normal px-2">Gold</th><th className="font-normal px-2">DXY</th>{H.map(([k, l]) => <th key={k} className="font-normal px-2">{l}</th>)}</tr></thead>
              <tbody>
                {d.events.slice(0, 12).map((e) => (
                  <tr key={e.date} className="border-t border-white/5">
                    <td className="py-1 pr-3 text-[#D1D5DB]">{day(e.date)}</td>
                    <td className="pr-3" style={{ color: DIR[e.direction][1] }}>{e.direction === "gold_above" ? "Gold above" : "Gold below"}</td>
                    <td className="text-center px-2 text-[#9CA3AF]">${e.gold.toFixed(1)}</td>
                    <td className="text-center px-2 text-[#9CA3AF]">{e.dxy.toFixed(2)}</td>
                    {H.map(([k]) => <td key={k} className="text-center px-2" style={{ color: col(e.returns[k]) }}>{pct(e.returns[k])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-[#9CA3AF] leading-relaxed">{d.note} Past results do not guarantee future moves — use this to judge how much weight the crossing deserves.</p>
          {d.legs && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-1">Hold crossing-to-crossing · last 10 position legs</div>
              <div className="overflow-x-auto">
                <table data-testid="crossing-legs-table" className="w-full text-[11px] font-mono-num whitespace-nowrap">
                  <thead><tr className="text-[#6B7280]"><th className="text-left font-normal py-1 pr-3">From</th><th className="text-left font-normal pr-3">To</th><th className="text-left font-normal pr-3">Side</th><th className="font-normal px-2">Entry</th><th className="font-normal px-2">Exit</th><th className="font-normal px-2">Days</th><th className="font-normal px-2">P/L $/oz</th><th className="font-normal px-2">P/L %</th></tr></thead>
                  <tbody>
                    {d.legs.slice(0, 10).map((l) => (
                      <tr key={l.from} className="border-t border-white/5">
                        <td className="py-1 pr-3 text-[#D1D5DB]">{day(l.from)}</td>
                        <td className="pr-3 text-[#D1D5DB]">{l.open ? <span className="text-[#FB923C]">open</span> : day(l.to)}</td>
                        <td className="pr-3" style={{ color: l.direction === "LONG" ? "#10B981" : "#EF4444" }}>{l.direction}</td>
                        <td className="text-center px-2 text-[#9CA3AF]">${l.entry.toFixed(1)}</td>
                        <td className="text-center px-2 text-[#9CA3AF]">${l.exit.toFixed(1)}</td>
                        <td className="text-center px-2 text-[#9CA3AF]">{l.days}</td>
                        <td className="text-center px-2 font-semibold" style={{ color: col(l.pnl_usd) }}>{l.pnl_usd > 0 ? "+" : ""}{l.pnl_usd.toFixed(1)}</td>
                        <td className="text-center px-2" style={{ color: col(l.pnl_pct) }}>{pct(l.pnl_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
